/**
 * SEO ヘルパー: TanStack Start の `head()` オプションに渡す meta/links を生成する。
 *
 * 使い方:
 *   export const Route = createFileRoute("/about")({
 *     head: () => seoHead({
 *       title: "About | KAGEM",
 *       description: "...",
 *       path: "/about",
 *     }),
 *   });
 */

export const SITE_URL =
  (typeof process !== "undefined" && process.env?.VITE_SITE_URL) ||
  (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SITE_URL) ||
  "https://kagem.ai";

export const SITE_NAME = "KAGEM";
export const DEFAULT_OG_IMAGE = "/og/landing.jpg";
export const TWITTER_SITE = "@kagem";

export interface SeoOptions {
  title?: string;
  description?: string;
  /** ルートパス（先頭スラッシュ付き）。canonical/og:url の生成に使う */
  path?: string;
  ogImage?: string;
  /** 検索エンジンにインデックスさせない */
  noindex?: boolean;
  /** og:type デフォルトは website */
  ogType?: "website" | "article";
}

export interface HeadMeta {
  meta?: Array<Record<string, string>>;
  links?: Array<Record<string, string>>;
  scripts?: Array<Record<string, string>>;
}

const FALLBACK_TITLE = "KAGEM - AIで作る変身動画";
const FALLBACK_DESCRIPTION =
  "日本語対応のAI変身動画作成サービス。写真と動画をアップロードするだけで簡単に高品質な変身動画を生成。";

export function absoluteUrl(path?: string): string {
  if (!path) return SITE_URL;
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function seoHead(opts: SeoOptions = {}): HeadMeta {
  const title = opts.title ?? FALLBACK_TITLE;
  const description = opts.description ?? FALLBACK_DESCRIPTION;
  const url = absoluteUrl(opts.path);
  const ogImage = absoluteUrl(opts.ogImage ?? DEFAULT_OG_IMAGE);
  const ogType = opts.ogType ?? "website";

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:type", content: ogType },
    { property: "og:locale", content: "ja_JP" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: ogImage },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: TWITTER_SITE },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
    { name: "twitter:creator", content: TWITTER_SITE },
  ];

  if (opts.noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  } else {
    meta.push({ name: "robots", content: "index, follow" });
  }

  const links: Array<Record<string, string>> = [{ rel: "canonical", href: url }];

  return { meta, links };
}
